import {
  clampPointToRect,
  easeRect,
  labelOffView,
  pointInRect,
  titleShouldPullOnView,
} from '../tagLayout';
import { applyTagLocal } from './geometry';
import { bunchCenterNdc, ndcToLocalOnBunch, projectLocal, tagPixelBox, visibleCloudRect } from './project';
import { VIEW_FOLLOW } from './vectors';

export function clearViewHold(bunch) {
  bunch.userData.tagHoldNdc = null;
  bunch.userData.tagHoldRect = null;
}

export function setTitleShown(tag, on) {
  const el = tag?.element;
  if (!el || el.dataset.editing === '1') return;
  el.style.visibility = on ? 'visible' : 'hidden';
  if (!on) el.style.opacity = '0';
}

export function pinTitleLocal(bunch, tag) {
  clearViewHold(bunch);
  if (bunch.userData.tagLocalX != null) {
    tag.position.set(bunch.userData.tagLocalX, bunch.userData.tagLocalY, bunch.userData.tagZ ?? 0.45);
    return true;
  }
  return false;
}

export function holdTitleInView(camera, bunch, tag, lwPx, lhPx, cw, ch, pad, titleBoxes, topInset = 0, leftInset = 0) {
  const lw = Math.min(0.42, lwPx / cw * 2) + pad;
  const lh = (lhPx / ch * 2) + pad;
  const raw = visibleCloudRect(camera, bunch, lw, lh, pad, topInset, leftInset);
  if (!raw) {
    clearViewHold(bunch);
    return false;
  }
  const rect = easeRect(bunch.userData.tagHoldRect, raw, 0.22);
  bunch.userData.tagHoldRect = rect;
  const last = {
    x: bunch.userData.tagLocalX ?? tag.position.x,
    y: bunch.userData.tagLocalY ?? tag.position.y,
    z: bunch.userData.tagZ ?? 0.45,
  };
  const projected = projectLocal(camera, bunch, last);
  let ndc = bunch.userData.tagHoldNdc || projected;
  if (!ndc) ndc = { x: (rect.left + rect.right) * 0.5, y: rect.top };
  const holding = !!bunch.userData.tagHoldNdc;
  const onSlice = pointInRect(ndc.x, ndc.y, rect)
    && labelOffView(ndc.x, ndc.y, lw, lh, topInset, leftInset) <= 1e-4;
  if (!holding && onSlice) {
    clearViewHold(bunch);
    return false;
  }
  if (!onSlice) {
    const center = bunchCenterNdc(camera, bunch);
    const clamp = clampPointToRect(center?.x ?? ndc.x, center?.y ?? ndc.y, rect);
    ndc = {
      x: ndc.x + (clamp.x - ndc.x) * VIEW_FOLLOW,
      y: ndc.y + (clamp.y - ndc.y) * VIEW_FOLLOW,
    };
  }
  bunch.userData.tagHoldNdc = ndc;
  const local = ndcToLocalOnBunch(camera, bunch, ndc.x, ndc.y);
  if (!local) return false;
  applyTagLocal(bunch, tag, local);
  titleBoxes.set(bunch, tagPixelBox({ x: ndc.x, y: ndc.y, z: 0 }, lwPx, lhPx, cw, ch));
  return true;
}

/**
 * Idle orbit: keep the world seat unless the title has left the usable view.
 * Then snap once in world space — no NDC hold, so the name does not twitch.
 */
export function keepTitleInView(camera, bunch, tag, lwPx, lhPx, cw, ch, pad, titleBoxes, topInset, leftInset = 0) {
  const lw = Math.min(0.42, lwPx / cw * 2) + pad;
  const lh = (lhPx / ch * 2) + pad;
  const last = {
    x: bunch.userData.tagLocalX ?? tag.position.x,
    y: bunch.userData.tagLocalY ?? tag.position.y,
    z: bunch.userData.tagZ ?? 0.45,
  };
  const projected = projectLocal(camera, bunch, last);
  const off = projected ? labelOffView(projected.x, projected.y, lw, lh, topInset, leftInset) : 1;
  const pulling = !!bunch.userData.tagViewPull;
  if (!titleShouldPullOnView(off, pulling)) {
    bunch.userData.tagViewPull = false;
    if (projected) titleBoxes.set(bunch, tagPixelBox({ x: projected.x, y: projected.y, z: 0 }, lwPx, lhPx, cw, ch));
    return;
  }
  bunch.userData.tagViewPull = true;
  const raw = visibleCloudRect(camera, bunch, lw, lh, pad, topInset, leftInset);
  if (!raw) return;
  const ndc = clampPointToRect(
    projected?.x ?? (raw.left + raw.right) * 0.5,
    projected?.y ?? raw.top,
    raw,
  );
  const local = ndcToLocalOnBunch(camera, bunch, ndc.x, ndc.y);
  if (!local) return;
  applyTagLocal(bunch, tag, local);
  titleBoxes.set(bunch, tagPixelBox({ x: ndc.x, y: ndc.y, z: 0 }, lwPx, lhPx, cw, ch));
}

/** A tagged cloud that is on the canvas must keep its name in the usable view. */
export function ensureTitleOnView(camera, bunch, tag, lwPx, lhPx, cw, ch, pad, titleBoxes, topInset, leftInset = 0) {
  const lw = Math.min(0.42, lwPx / cw * 2) + pad;
  const lh = (lhPx / ch * 2) + pad;
  const last = {
    x: bunch.userData.tagLocalX ?? tag.position.x,
    y: bunch.userData.tagLocalY ?? tag.position.y,
    z: bunch.userData.tagZ ?? 0.45,
  };
  const projected = projectLocal(camera, bunch, last);
  if (projected && labelOffView(projected.x, projected.y, lw, lh, topInset, leftInset) <= 1e-4) {
    titleBoxes.set(bunch, tagPixelBox({ x: projected.x, y: projected.y, z: 0 }, lwPx, lhPx, cw, ch));
    return;
  }
  if (holdTitleInView(camera, bunch, tag, lwPx, lhPx, cw, ch, pad, titleBoxes, topInset, leftInset)) return;
  const raw = visibleCloudRect(camera, bunch, lw, lh, pad, topInset, leftInset);
  if (!raw) return;
  const ndc = clampPointToRect(
    projected?.x ?? (raw.left + raw.right) * 0.5,
    projected?.y ?? raw.top,
    raw,
  );
  const local = ndcToLocalOnBunch(camera, bunch, ndc.x, ndc.y);
  if (!local) return;
  applyTagLocal(bunch, tag, local);
  titleBoxes.set(bunch, tagPixelBox({ x: ndc.x, y: ndc.y, z: 0 }, lwPx, lhPx, cw, ch));
}
